/**
 * Los fallos del API, en una sola forma.
 *
 * El sobre es el de lala-ms (§10.4):
 *
 *     { success: false,
 *       error: { code, message, errors?, errorId, timestamp, path, details? } }
 *
 * `code` es el error CONCRETO (`CONVERSATION_NOT_FOUND`), no la familia; la
 * familia la dice el estado HTTP. Por eso aquí las decisiones --reintentar,
 * refrescar la sesión, hablar de permisos-- se toman mirando `status`, y
 * `code` queda para quien espere un error en particular.
 *
 * Lo que no traiga `success: false` --el 502 del gateway, un 413 de Nginx, un
 * HTML de error-- NO es nuestro sobre, aunque se le parezca: se normaliza por
 * estado en vez de fiarse de campos que nadie promete.
 */

/** Una incidencia de validación: qué campo, qué le pasa. */
export interface FieldError {
  readonly field: string;
  readonly message: string;
  readonly code?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly errors: readonly FieldError[];
  /** El `correlationId` de la petición: con él se busca en los logs. */
  readonly errorId: string | undefined;
  readonly details: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    extra: {
      errors?: readonly FieldError[];
      errorId?: string | undefined;
      details?: unknown;
    } = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.errors = extra.errors ?? [];
    this.errorId = extra.errorId;
    this.details = extra.details;
  }
}

/** Los estados que la interfaz trata aparte. */
export const HTTP = {
  badRequest: 400,
  unauthorized: 401,
  forbidden: 403,
  notFound: 404,
  conflict: 409,
  unprocessable: 422,
  internal: 500,
  badGateway: 502,
  unavailable: 503,
  gatewayTimeout: 504,
} as const;

/**
 * Qué decir cuando el cuerpo no es nuestro sobre. El código imita los de
 * lala-ms para que quien compare `code` no tenga que saber de dónde vino.
 */
const BY_STATUS: Record<number, { code: string; message: string }> = {
  [HTTP.badRequest]: {
    code: "VALIDATION_ERROR",
    message: "La petición no es válida.",
  },
  [HTTP.unauthorized]: {
    code: "UNAUTHORIZED",
    message: "Tu sesión ha caducado. Vuelve a entrar.",
  },
  [HTTP.forbidden]: {
    code: "FORBIDDEN",
    message: "No tienes permiso para hacer esto.",
  },
  [HTTP.notFound]: { code: "NOT_FOUND", message: "No existe." },
  [HTTP.conflict]: {
    code: "CONFLICT",
    message: "Alguien lo cambió mientras tanto. Recarga y vuelve a probar.",
  },
  [HTTP.unprocessable]: {
    code: "BUSINESS_RULE",
    message: "No se puede hacer en este estado.",
  },
  [HTTP.badGateway]: {
    code: "BAD_GATEWAY",
    message: "El servidor no responde. Prueba en un momento.",
  },
  [HTTP.unavailable]: {
    code: "SERVICE_UNAVAILABLE",
    message: "El servicio no está disponible. Prueba en un momento.",
  },
  [HTTP.gatewayTimeout]: {
    code: "GATEWAY_TIMEOUT",
    message: "El servidor tardó demasiado en responder.",
  },
};

const fallbackFor = (status: number): { code: string; message: string } =>
  BY_STATUS[status] ?? {
    code: status >= HTTP.internal ? "INTERNAL_ERROR" : "UNKNOWN_ERROR",
    message: `El servidor respondió con un error (${String(status)}).`,
  };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const text = (value: unknown): string | undefined =>
  typeof value === "string" && value !== "" ? value : undefined;

const fieldErrors = (value: unknown): FieldError[] =>
  Array.isArray(value)
    ? value.flatMap((item): FieldError[] => {
        if (!isRecord(item)) return [];
        const field = text(item["field"]);
        const message = text(item["message"]);
        if (field === undefined || message === undefined) return [];
        const code = text(item["code"]);
        return [{ field, message, ...(code === undefined ? {} : { code }) }];
      })
    : [];

/** Un `ApiError` a partir del estado y de lo que haya venido en el cuerpo. */
export const apiErrorFrom = (status: number, body: unknown): ApiError => {
  const fallback = fallbackFor(status);
  const error =
    isRecord(body) && body["success"] === false && isRecord(body["error"])
      ? body["error"]
      : undefined;

  if (error === undefined) {
    return new ApiError(status, fallback.code, fallback.message);
  }

  return new ApiError(
    status,
    text(error["code"]) ?? fallback.code,
    text(error["message"]) ?? fallback.message,
    {
      errors: fieldErrors(error["errors"]),
      errorId: text(error["errorId"]),
      details: error["details"],
    },
  );
};

/** Lee el cuerpo de una respuesta fallida, sea lo que sea, y la traduce. */
export const readApiError = async (response: Response): Promise<ApiError> =>
  apiErrorFrom(
    response.status,
    await response.json().catch((): unknown => undefined),
  );

/**
 * El mensaje de un fallo, en la forma en que se le enseña a una persona.
 *
 * Un 403 habla SIEMPRE de permisos, diga lo que diga el cuerpo: el mensaje del
 * servidor suele nombrar el permiso técnico (`socio-amigo:batches.apply`), que
 * no le dice nada a quien lo lee. Un fallo del servidor lleva su referencia,
 * para que quien lo reporte pueda dar algo que buscar en los logs.
 */
export const errorMessage = (error: unknown): string => {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : String(error);
  }

  if (error.status === HTTP.forbidden) {
    return "No tienes permiso para hacer esto. Si crees que deberías, pídeselo a quien administra el programa.";
  }

  const fields =
    error.errors.length === 0
      ? ""
      : ` ${error.errors.map((item) => `${item.field}: ${item.message}`).join("; ")}.`;
  const reference =
    error.status >= HTTP.internal && error.errorId !== undefined
      ? ` (referencia ${error.errorId})`
      : "";

  return `${error.message}${fields}${reference}`;
};

/**
 * Si merece la pena volver a pedirlo. Solo lo que pinta a problema de red o a
 * un servidor de paso caído; un 404 o un 422 no mejoran por insistir.
 */
export const isTransient = (error: unknown): boolean =>
  !(error instanceof ApiError) ||
  error.status === HTTP.badGateway ||
  error.status === HTTP.unavailable ||
  error.status === HTTP.gatewayTimeout;
